/* eslint-disable camelcase */
import React from 'react';
import PropTypes from 'prop-types';

import { useGetMatchRosterRoundQuery } from 'common/store/modules/api/cfs-api';
import { useSingleSiteSetting } from 'common/react/hooks/use-single-site-setting';
import { getTranslation } from 'common/react/utils/translations';

import { TLCondensedCta } from '../TLCondensedCta';
import { InsOuts } from '../InsOuts/InsOuts';
import { TLCondensedLastUpdatedSkeleton } from '../skeleton-loaders/TLCondensedLastUpdatedSkeleton';
import { InsOutsSingleSkeleton } from '../skeleton-loaders/InsOutsSingleSkeleton';
import { InsOutsBothSkeleton } from '../skeleton-loaders/InsOutsBothSkeleton';
import { TLCondensedCtaSkeleton } from '../skeleton-loaders/TLCondensedCtaSkeleton';
import { TLCondensedHeader } from '../TLCondensedHeader';
import { TLCondensedHeaderSkeleton } from '../skeleton-loaders/TLCondensedHeaderSkeleton';
import { LastUpdated } from 'common/react/components/LastUpdated';
import { Sponsor } from 'common/react/components/sponsor/Sponsor';

/**
 * Fetches the match roster data.
 *
 * @param {string} matchId The Pulse match ID.
 * @param {string} matchTitle The match title, e.g. 'Sydney Swans v GWS GIANTS'.
 * @param {string} matchUrl The match URL.
 * @param {string} roundName The round name, e.g. 'Round 7'.
 * @param {string} providerMatchId The provider match ID.
 * @param {string} providerRoundId The provider round ID.
 * @param {string} teamDisplay The team(s) to display, i.e. 'both', 'home', or 'away'.
 * @param {string} startTime The match start time.
 * @param {string} venueName The venue name, e.g. 'SCG'.
 * @param {string} venueLocation The venue location, e.g. 'Sydney'.
 * @param {string} venueLandOwner The venue landowner, e.g. 'Gadigal'.
 * @param {boolean} ctaDisabled If the CTA has been disabled.
 * @param {string} ctaCustomLink The custom link for the CTA.
 * @param {string} ctaCustomText The custom text for the CTA.
 * @param {string} sponsorPromoId The promo ID to render as a sponsor.
 * @returns {JSX.Element} The empty state or the condensed team lineups.
 */
export const TLCondensed = ({
    matchId,
    matchTitle,
    matchUrl,
    roundName,
    providerMatchId,
    providerRoundId,
    homeTeamId,
    awayTeamId,
    homeTeamName,
    awayTeamName,
    teamDisplay,
    ctaDefaultPath,
    ctaDisabled,
    ctaCustomLink,
    ctaCustomText,
    startTime,
    venueName,
    venueLocation,
    venueLandOwner,
    sponsorPromoId
}) => {
    const {
        data: rosterData,
        isFetching: isFetchingMatchRosterData,
        isError: matchRosterDataError
    } = useGetMatchRosterRoundQuery(
        { roundId: providerRoundId },
        { skip: !providerRoundId }
    );

    const matchRoster = rosterData?.find(
            (roster) => roster.match.matchId === providerMatchId
        )?.matchRoster,
        selectedRegion = useSingleSiteSetting('region'),
        lastUpdated = moment(matchRoster?.lastUpdated),
        insOutsExists =
            matchRoster?.homeTeam?.ins?.length > 0 ||
            matchRoster?.homeTeam?.outs?.length > 0 ||
            matchRoster?.awayTeam?.ins?.length > 0 ||
            matchRoster?.awayTeam?.outs?.length > 0,
        ctaDefaultLink = `${ctaDefaultPath}?fixtureId=${matchId}`,
        ctaConfig = !ctaDisabled
            ? {
                  ctaLink: ctaCustomLink || ctaDefaultLink,
                  ctaText:
                      ctaCustomText ||
                      getTranslation('label.teamLineups.viewFull'),
                  ctaDefaultPath: ctaCustomLink ? 'false' : 'true'
              }
            : null,
        headerData = {
            startTime: startTime,
            venueName: venueName,
            venueLocation: venueLocation,
            venueLandOwner: venueLandOwner,
            homeTeamId: homeTeamId,
            awayTeamId: awayTeamId,
            matchUrl: matchUrl,
            roundName: roundName,
            homeTeamName: homeTeamName,
            awayTeamName: awayTeamName
        };

    return (
        <>
            {/* Always show the header component once data fetching is complete */}
            {!isFetchingMatchRosterData && (
                <>
                    <TLCondensedHeader
                        match={headerData}
                        teamDisplay={teamDisplay}
                    />
                    {sponsorPromoId && (
                        <Sponsor
                            promoId={sponsorPromoId}
                            cssClass="team-lineups-condensed-embeddable"
                        />
                    )}
                </>
            )}

            {isFetchingMatchRosterData ? (
                teamDisplay === 'both' ? (
                    <>
                        <TLCondensedHeaderSkeleton teamDisplay={teamDisplay} />
                        <InsOutsBothSkeleton />
                        <InsOutsBothSkeleton />
                        <TLCondensedCtaSkeleton />
                        <TLCondensedLastUpdatedSkeleton />
                    </>
                ) : (
                    <>
                        <TLCondensedHeaderSkeleton teamDisplay={teamDisplay} />
                        <InsOutsSingleSkeleton />
                        <TLCondensedCtaSkeleton />
                        <TLCondensedLastUpdatedSkeleton />
                    </>
                )
            ) : matchRosterDataError || !rosterData || !matchRoster ? (
                <>
                    <div className="team-lineups__section">
                        <div className="team-lineups-condensed-embeddable__no-data">
                            <p className="team-lineups-condensed-embeddable__no-data-text">
                                {getTranslation(
                                    'label.teamLineups.notAvailable'
                                )}
                            </p>
                        </div>
                        {ctaConfig && <TLCondensedCta ctaConfig={ctaConfig} />}
                    </div>
                    <LastUpdated
                        cssClass="team-lineups__last-updated"
                        lastUpdated={lastUpdated}
                        selectedRegion={selectedRegion}
                    />
                </>
            ) : rosterData ? (
                insOutsExists ? (
                    <>
                        <InsOuts
                            matchRoster={matchRoster}
                            matchTitle={matchTitle}
                            teamDisplay={teamDisplay}
                            ctaConfig={ctaConfig}
                        />
                        <LastUpdated
                            cssClass="team-lineups__last-updated"
                            lastUpdated={lastUpdated}
                            selectedRegion={selectedRegion}
                        />
                    </>
                ) : (
                    <>
                        <div className="team-lineups__section">
                            <div className="team-lineups-condensed-embeddable__no-changes">
                                <p className="team-lineups-condensed-embeddable__no-changes-text">
                                    {getTranslation(
                                        'label.teamLineups.noChanges'
                                    )}
                                </p>
                            </div>
                            {ctaConfig && (
                                <TLCondensedCta
                                    ctaConfig={ctaConfig}
                                    matchTitle={matchTitle}
                                />
                            )}
                        </div>
                        <LastUpdated
                            cssClass="team-lineups__last-updated"
                            lastUpdated={lastUpdated}
                            selectedRegion={selectedRegion}
                        />
                    </>
                )
            ) : null}
        </>
    );
};

TLCondensed.propTypes = {
    matchId: PropTypes.string.isRequired,
    matchTitle: PropTypes.string.isRequired,
    matchUrl: PropTypes.string.isRequired,
    roundName: PropTypes.string.isRequired,
    providerMatchId: PropTypes.string.isRequired,
    providerRoundId: PropTypes.string.isRequired,
    homeTeamId: PropTypes.string.isRequired,
    awayTeamId: PropTypes.string.isRequired,
    homeTeamName: PropTypes.string.isRequired,
    awayTeamName: PropTypes.string.isRequired,
    teamDisplay: PropTypes.string.isRequired,
    ctaDefaultPath: PropTypes.string,
    ctaDisabled: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    ctaCustomLink: PropTypes.string,
    ctaCustomText: PropTypes.string,
    startTime: PropTypes.string.isRequired,
    venueName: PropTypes.string.isRequired,
    venueLocation: PropTypes.string.isRequired,
    venueLandOwner: PropTypes.string.isRequired,
    sponsorPromoId: PropTypes.string
};
